import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../../ui';
import { useAuth } from '../../../stores';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const { sendPasswordReset, isLoading, error, clearError } = useAuth();
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    clearError();
    try {
      await sendPasswordReset(data.email);
      setEmailSent(true);
      setSentEmail(data.email);
    } catch {
      // Error is handled by the store
    }
  };

  if (emailSent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-surface-800 rounded-2xl p-8 shadow-sm border border-surface-200 dark:border-surface-700 text-center"
      >
        <div className="w-16 h-16 mx-auto bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-6">
          <CheckCircleIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        <h2 className="text-xl font-semibold text-surface-900 dark:text-white mb-2">
          Check your email
        </h2>
        <p className="text-surface-600 dark:text-surface-400 mb-6">
          We've sent password reset instructions to{' '}
          <span className="font-medium text-surface-900 dark:text-white">{sentEmail}</span>
        </p>
        <p className="text-sm text-surface-500 dark:text-surface-400 mb-6">
          Didn't receive the email? Check your spam folder or{' '}
          <button
            onClick={() => {
              setEmailSent(false);
              clearError();
            }}
            className="text-accent-600 dark:text-accent-400 hover:underline font-medium"
          >
            try again
          </button>
        </p>
        <Link to="/login">
          <Button variant="secondary" size="lg" className="w-full">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to sign in
          </Button>
        </Link>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-white dark:bg-surface-800 rounded-2xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
        <p className="text-surface-600 dark:text-surface-400 mb-6 text-center">
          Enter your email address and we'll send you instructions to reset your password.
        </p>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
              <EnvelopeIcon className="w-5 h-5" />
            </div>
            <Input
              type="email"
              placeholder="Email address"
              className="pl-10"
              {...register('email')}
              error={errors.email?.message}
            />
          </div>
        </motion.div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-xl p-4"
        >
          <p className="text-sm text-danger-600 dark:text-danger-400 text-center">
            {error}
          </p>
        </motion.div>
      )}

      <Button type="submit" size="lg" className="w-full" isLoading={isLoading}>
        Send Reset Instructions
      </Button>

      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to sign in
        </Link>
      </div>
    </form>
  );
}
